import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material'
import {
  Visibility,
  FilterList,
  Download,
  Refresh
} from '@mui/icons-material'
import { format } from 'date-fns'
import axios from 'axios'
import { QRCodeSVG } from 'qrcode.react'
import { API_BASE_URL } from '../config/api'

interface Bid {
  id: string
  bid_number?: string
  lot_id: string
  bidder_id: string
  bid_amount: number
  bid_quantity: number
  total_bid_amount: number
  bid_type: string
  bid_time: string
  lot_name: string
  auction_type: string
  auction_status: string
  bid_status: string
  farmer_name: string
  bidder_name?: string
  bidder_email?: string
}

const AllBids: React.FC = () => {
  const [bids, setBids] = useState<Bid[]>([])
  const [filteredBids, setFilteredBids] = useState<Bid[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [auctionTypeFilter, setAuctionTypeFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')

  // Statistics
  const [stats, setStats] = useState({
    totalBids: 0,
    totalValue: 0,
    wonBids: 0,
    activeBids: 0,
    dutchBids: 0,
    englishBids: 0
  })

  useEffect(() => {
    fetchBids()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [bids, searchTerm, statusFilter, auctionTypeFilter, startDate, endDate, minPrice, maxPrice])

  const fetchBids = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      
      const response = await axios.get(`${API_BASE_URL}/harvests/bids/all`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.success) {
        setBids(response.data.data)
        calculateStats(response.data.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bids')
      console.error('Error fetching bids:', err)
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (bidsData: Bid[]) => {
    const totalBids = bidsData.length
    const totalValue = bidsData.reduce((sum, bid) => sum + (Number(bid.total_bid_amount) || 0), 0)
    const wonBids = bidsData.filter(bid => bid.bid_status === 'won').length
    const activeBids = bidsData.filter(bid => ['active', 'winning'].includes(bid.bid_status)).length
    const dutchBids = bidsData.filter(bid => bid.auction_type === 'dutch').length
    const englishBids = bidsData.filter(bid => bid.auction_type === 'english').length

    setStats({
      totalBids,
      totalValue,
      wonBids,
      activeBids,
      dutchBids,
      englishBids
    })
  }

  const applyFilters = () => {
    let filtered = [...bids]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(bid =>
        bid.lot_name?.toLowerCase().includes(search) ||
        bid.farmer_name?.toLowerCase().includes(search) ||
        bid.bidder_name?.toLowerCase().includes(search) ||
        bid.bidder_email?.toLowerCase().includes(search) ||
        bid.id.toLowerCase().includes(search)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(bid => bid.bid_status === statusFilter)
    }

    // Auction type filter
    if (auctionTypeFilter !== 'all') {
      filtered = filtered.filter(bid => bid.auction_type === auctionTypeFilter)
    }

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter(bid => {
        const bidDate = new Date(bid.bid_time)
        const start = startDate ? new Date(startDate) : null
        const end = endDate ? new Date(endDate) : null
        
        if (start && end) {
          return bidDate >= start && bidDate <= end
        } else if (start) {
          return bidDate >= start
        } else if (end) {
          return bidDate <= end
        }
        return true
      })
    }

    // Price filter
    if (minPrice) {
      filtered = filtered.filter(bid => bid.bid_amount >= parseFloat(minPrice))
    }
    if (maxPrice) {
      filtered = filtered.filter(bid => bid.bid_amount <= parseFloat(maxPrice))
    }

    setFilteredBids(filtered)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewDetails = (bid: Bid) => {
    setSelectedBid(bid)
    setDetailsOpen(true)
  }

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'info' | 'warning' | 'error' | 'default' } = {
      won: 'success',
      winning: 'info',
      active: 'info',
      outbid: 'warning',
      lost: 'error',
      pending: 'default'
    }
    return colors[status] || 'default'
  }

  const exportToCSV = () => {
    const headers = ['Bid ID', 'Auction', 'Bidder', 'Price/kg', 'Quantity', 'Total Amount', 'Type', 'Status', 'Date']
    const rows = filteredBids.map(bid => [
      bid.id,
      bid.lot_name,
      bid.bidder_name || bid.bidder_email || 'Unknown',
      bid.bid_amount,
      bid.bid_quantity,
      bid.total_bid_amount,
      bid.auction_type,
      bid.bid_status,
      format(new Date(bid.bid_time), 'yyyy-MM-dd HH:mm')
    ])

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bids_${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          All Bids Management
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchBids} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton onClick={exportToCSV} color="primary">
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Bids
              </Typography>
              <Typography variant="h5">{stats.totalBids}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Value
              </Typography>
              <Typography variant="h5">${Number(stats.totalValue || 0).toFixed(2).replace(/^0+/, '').replace(/^\./, '0.')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Won Bids
              </Typography>
              <Typography variant="h5" color="success.main">{stats.wonBids}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Active Bids
              </Typography>
              <Typography variant="h5" color="info.main">{stats.activeBids}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Dutch Auctions
              </Typography>
              <Typography variant="h5">{stats.dutchBids}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                English Auctions
              </Typography>
              <Typography variant="h5">{stats.englishBids}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterList />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          {/* First Row */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Auction, bidder, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="won">Won</MenuItem>
                <MenuItem value="winning">Winning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="outbid">Outbid</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Auction Type</InputLabel>
              <Select
                value={auctionTypeFilter}
                label="Auction Type"
                onChange={(e) => setAuctionTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="dutch">Dutch</MenuItem>
                <MenuItem value="english">English</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Second Row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Min Price"
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Max Price"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Bids Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bid ID</TableCell>
              <TableCell>Auction</TableCell>
              <TableCell>Bidder</TableCell>
              <TableCell>Price/kg</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBids
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((bid) => (
                <TableRow key={bid.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                      {bid.id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{bid.lot_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{bid.bidder_name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {bid.bidder_email}
                    </Typography>
                  </TableCell>
                  <TableCell>${Number(bid.bid_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(bid.bid_quantity || 0).toFixed(2)} kg</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${Number(bid.total_bid_amount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bid.auction_type}
                      size="small"
                      color={bid.auction_type === 'dutch' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bid.bid_status}
                      size="small"
                      color={getStatusColor(bid.bid_status)}
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(bid.bid_time), 'MMM dd, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(bid)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {filteredBids.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary" py={4}>
                    No bids found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredBids.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Bid Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Bid Details</Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedBid && format(new Date(selectedBid.bid_time), 'MMM dd, yyyy HH:mm:ss')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBid && (
            <Box>
              {/* Bid Number & QR Code Section */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'secondary.50', border: '2px solid', borderColor: 'secondary.main' }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Bid Number
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="secondary" fontFamily="monospace" sx={{ letterSpacing: 2 }}>
                      {selectedBid.bid_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'white' }}>
                        <QRCodeSVG
                          value={JSON.stringify({
                            type: 'bid',
                            id: selectedBid.id,
                            number: selectedBid.bid_number,
                            timestamp: new Date().toISOString()
                          })}
                          size={120}
                          level="H"
                          includeMargin
                        />
                      </Paper>
                      <Typography variant="caption" color="textSecondary" align="center">
                        Scan for verification
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 3 }} />

              {/* Auction & Parties Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Auction & Parties
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Auction Lot
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {selectedBid.lot_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Bidder
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedBid.bidder_name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedBid.bidder_email}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Farmer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedBid.farmer_name}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Bid Details Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Bid Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Price per kg
                    </Typography>
                    <Typography variant="h6">
                      ${Number(selectedBid.bid_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Quantity
                    </Typography>
                    <Typography variant="h6">
                      {Number(selectedBid.bid_quantity || 0).toFixed(2)} kg
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Total Bid Amount
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        ${Number(selectedBid.total_bid_amount || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Status Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Status Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Auction Type
                    </Typography>
                    <Chip
                      label={selectedBid.auction_type}
                      color={selectedBid.auction_type === 'dutch' ? 'primary' : 'secondary'}
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Bid Status
                    </Typography>
                    <Chip
                      label={selectedBid.bid_status}
                      color={getStatusColor(selectedBid.bid_status)}
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailsOpen(false)} variant="contained" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AllBids
